import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Shield, Wrench, FileText, Building2, Car, Coffee, ShoppingBag, Factory, ArrowUpRight } from 'lucide-react';
export function CaseStudies() {
  const [activeFilter, setActiveFilter] = useState('All');
  const filters = ['All', 'Installation', 'Maintenance', 'TM44/Compliance', 'Hospitality', 'Retail', 'Offices', 'Industrial'];
  const allProjects = [{
    sector: 'Offices',
    title: 'Corporate HQ climate overhaul',
    result: '30% reduction in energy costs and improved staff comfort.',
    tags: ['Installation', 'Energy Efficiency'],
    link: '/contact'
  }, {
    sector: 'Retail',
    title: 'High street store cooling',
    result: 'Zero downtime during peak summer trading.',
    tags: ['Maintenance', 'Retail'],
    link: '/contact'
  }, {
    sector: 'Industrial',
    title: 'Warehouse temperature control',
    result: 'Stabilized storage environment for sensitive goods.',
    tags: ['Installation', 'Industrial'],
    link: '/contact'
  }, {
    sector: 'Hospitality',
    title: 'Boutique hotel AC upgrade',
    result: 'Whisper-quiet operation enhancing guest experience.',
    tags: ['Installation', 'Hospitality'],
    link: '/contact'
  }, {
    sector: 'Offices',
    title: 'Multi-site TM44 assessments',
    result: 'Full compliance achieved across 15 locations.',
    tags: ['TM44/Compliance', 'Offices'],
    link: '/contact'
  }, {
    sector: 'Retail',
    title: 'Supermarket refrigeration & AC',
    result: 'Integrated maintenance improving overall reliability.',
    tags: ['Maintenance', 'Retail'],
    link: '/contact'
  }, {
    sector: 'Industrial',
    title: 'Manufacturing plant ventilation',
    result: 'Improved air quality meeting strict health & safety standards.',
    tags: ['Installation', 'Industrial'],
    link: '/contact'
  }, {
    sector: 'Hospitality',
    title: 'Restaurant chain PPM',
    result: 'Preventative maintenance reducing emergency call-outs by 40%.',
    tags: ['Maintenance', 'Hospitality'],
    link: '/contact'
  }];
  const filteredProjects = activeFilter === 'All' ? allProjects : allProjects.filter(p => p.sector === activeFilter || p.tags.includes(activeFilter));
  return <div className="pt-24 bg-white" data-id="element-959">
      {/* Section 1: Hero */}
      <section className="py-16 md:py-24 bg-primary-light overflow-hidden" data-id="element-960">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-961">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-962">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-963">
              <span className="text-accent font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-964">
                Case Studies
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-dark mb-6 leading-tight" data-id="element-965">
                Commercial HVAC projects delivered with quality, care and
                long-term value
              </h1>
              <p className="text-xl text-gray-700 mb-8" data-id="element-966">
                Explore a selection of our recent projects, demonstrating our
                commitment to bespoke design, reliable installation, and
                proactive maintenance across various commercial sectors.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-967">
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-white bg-accent hover:bg-accent/90 transition-colors" data-id="element-968">
                  Discuss your project
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-969" />
                </Link>
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-primary-dark border-2 border-primary-dark hover:bg-primary-dark hover:text-white transition-colors" data-id="element-970">
                  Book a site survey
                </Link>
              </div>

              <div className="flex flex-wrap gap-4" data-id="element-971">
                {['Nationwide commercial projects', 'Design-led solutions', 'Trusted by brands and FM teams'].map((pill, idx) => <div key={idx} className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm" data-id="element-972">
                    <CheckCircle2 className="w-4 h-4 text-accent mr-2" data-id="element-973" />
                    <span className="text-sm font-medium text-primary-dark" data-id="element-974">
                      {pill}
                    </span>
                  </div>)}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="relative" data-id="element-975">
              <div className="aspect-square md:aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl" data-id="element-976">
                <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80" alt="Modern commercial office space" className="w-full h-full object-cover" data-id="element-977" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 2: Value Strip */}
      <section className="py-12 border-b border-gray-200" data-id="element-978">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-979">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-980">
            {[{
            icon: Wrench,
            title: 'Bespoke commercial design',
            desc: 'Tailored solutions for unique environments.'
          }, {
            icon: Shield,
            title: 'Installations built to last',
            desc: 'High-quality equipment and rigorous standards.'
          }, {
            icon: FileText,
            title: 'Detailed reporting & compliance',
            desc: 'Comprehensive documentation for peace of mind.'
          }, {
            icon: Building2,
            title: 'Trusted by recognised brands',
            desc: 'Partnering with leading names across sectors.'
          }].map((value, idx) => <div key={idx} className="flex items-start" data-id="element-981">
                <div className="w-10 h-10 bg-primary-light rounded-lg flex items-center justify-center flex-shrink-0 mr-4" data-id="element-982">
                  <value.icon className="w-5 h-5 text-accent" data-id="element-983" />
                </div>
                <div data-id="element-984">
                  <h3 className="font-bold text-primary-dark mb-1" data-id="element-985">
                    {value.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-986">{value.desc}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Section 3: Featured Case Studies (Bento) */}
      <section className="py-20 bg-surface" data-id="element-987">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-988">
          <div className="text-center mb-12" data-id="element-989">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-4" data-id="element-990">
              Featured projects
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-991">
              Deep dives into some of our most impactful commercial HVAC
              installations.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-992">
            {/* Large Card */}
            <Link to="/case-studies/formula-one" className="lg:col-span-2 group block" data-id="element-993">
              <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col" data-id="element-994">
                <div className="relative h-64 sm:h-80 overflow-hidden" data-id="element-995">
                  <img src="https://cdn.magicpatterns.com/uploads/25TP9PqASwBfhD3FYFT9Py/pasted-image.jpg" alt="Automotive engineering" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-996" />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-sm font-semibold text-primary-dark" data-id="element-997">
                    Engineering / Performance Environment
                  </div>
                </div>
                <div className="p-8 flex-grow flex flex-col" data-id="element-998">
                  <h3 className="text-2xl font-bold text-primary-dark mb-3 group-hover:text-accent transition-colors" data-id="element-999">
                    Precision temperature control for a high-performance working
                    environment
                  </h3>
                  <p className="text-gray-600 mb-6 flex-grow" data-id="element-1000">
                    Designed to support comfort, air quality and tightly
                    controlled workspace conditions in a demanding engineering
                    setting.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-1001">
                    {['Bespoke design', 'Air quality focus', 'Performance workspace'].map((tag, i) => <span key={i} className="bg-surface px-3 py-1 rounded-full text-xs font-medium text-gray-600" data-id="element-1002">
                        {tag}
                      </span>)}
                  </div>
                  <div className="flex items-center text-accent font-semibold" data-id="element-1003">
                    View case study <ArrowRight className="ml-2 w-4 h-4" data-id="element-1004" />
                  </div>
                </div>
              </div>
            </Link>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6" data-id="element-1005">
              {/* Medium Card 1 */}
              <Link to="/case-studies/subway" className="group block" data-id="element-1006">
                <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col" data-id="element-1007">
                  <div className="relative h-48 overflow-hidden" data-id="element-1008">
                    <img src="https://cdn.magicpatterns.com/uploads/6woGoZZ58PZurSZ7Y9FmxV/subway-welcome.jpg" alt="Subway" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1009" />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold text-primary-dark" data-id="element-1010">
                      Hospitality / Quick Service
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-1011">
                    <h3 className="text-lg font-bold text-primary-dark mb-2 group-hover:text-accent transition-colors" data-id="element-1012">
                      Reliable cooling for a busy customer-facing food service
                      environment
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 flex-grow" data-id="element-1013">
                      Commercial climate solution designed to support customer
                      comfort, staff working conditions and day-to-day
                      reliability.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4" data-id="element-1014">
                      {['Customer-facing', 'Reliable cooling', 'Commercial install'].map((tag, i) => <span key={i} className="bg-surface px-2 py-1 rounded-full text-[10px] font-medium text-gray-600" data-id="element-1015">
                          {tag}
                        </span>)}
                    </div>
                    <div className="flex items-center text-accent font-semibold text-sm mt-auto" data-id="element-1016">
                      View case study <ArrowRight className="ml-1 w-4 h-4" data-id="element-1017" />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Medium Card 2 */}
              <Link to="/case-studies/process-cooling" className="group block" data-id="element-1018">
                <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col" data-id="element-1019">
                  <div className="relative h-48 overflow-hidden" data-id="element-1020">
                    <img src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80" alt="Process Cooling" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1021" />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold text-primary-dark" data-id="element-1022">
                      Industrial / Process Environment
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-1023">
                    <h3 className="text-lg font-bold text-primary-dark mb-2 group-hover:text-accent transition-colors" data-id="element-1024">
                      A cooling solution that supported operations and paid back
                      fast
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 flex-grow" data-id="element-1025">
                      Practical application-focused cooling that helped improve
                      process control and reduce waste in production.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4" data-id="element-1026">
                      {['Operational ROI', 'Process cooling', 'Commercial application'].map((tag, i) => <span key={i} className="bg-surface px-2 py-1 rounded-full text-[10px] font-medium text-gray-600" data-id="element-1027">
                          {tag}
                        </span>)}
                    </div>
                    <div className="flex items-center text-accent font-semibold text-sm mt-auto" data-id="element-1028">
                      View case study <ArrowRight className="ml-1 w-4 h-4" data-id="element-1029" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Brands by Sector */}
      <section className="py-20" data-id="element-1030">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1031">
          <div className="text-center mb-12" data-id="element-1032">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-4" data-id="element-1033">
              Brands and businesses we've supported
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1034">
              Delivering tailored climate solutions across diverse commercial
              sectors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1035">
            <div className="bg-surface p-8 rounded-3xl" data-id="element-1036">
              <div className="flex items-center mb-4" data-id="element-1037">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4" data-id="element-1038">
                  <Building2 className="w-5 h-5 text-accent" data-id="element-1039" />
                </div>
                <h3 className="text-xl font-bold text-primary-dark" data-id="element-1040">
                  Hospitality & hotels
                </h3>
              </div>
              <p className="text-gray-600 mb-6" data-id="element-1041">
                Guest-facing hospitality settings where comfort and uptime are
                essential.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-1042">
                {['Ramada', 'Days Inn'].map((brand, i) => <span key={i} className="bg-white px-3 py-1 rounded-full text-sm font-medium text-primary-dark border border-gray-200" data-id="element-1043">
                    {brand}
                  </span>)}
              </div>
            </div>

            <div className="bg-surface p-8 rounded-3xl" data-id="element-1044">
              <div className="flex items-center mb-4" data-id="element-1045">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4" data-id="element-1046">
                  <ShoppingBag className="w-5 h-5 text-accent" data-id="element-1047" />
                </div>
                <h3 className="text-xl font-bold text-primary-dark" data-id="element-1048">
                  Retail & public-facing
                </h3>
              </div>
              <p className="text-gray-600 mb-6" data-id="element-1049">
                Commercial environments where customer experience and dependable
                climate control matter.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-1050">
                {['Post Office', 'Specsavers', 'Cotswold Company', 'Toni & Guy'].map((brand, i) => <span key={i} className="bg-white px-3 py-1 rounded-full text-sm font-medium text-primary-dark border border-gray-200" data-id="element-1051">
                    {brand}
                  </span>)}
              </div>
            </div>

            <div className="bg-surface p-8 rounded-3xl" data-id="element-1052">
              <div className="flex items-center mb-4" data-id="element-1053">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4" data-id="element-1054">
                  <Coffee className="w-5 h-5 text-accent" data-id="element-1055" />
                </div>
                <h3 className="text-xl font-bold text-primary-dark" data-id="element-1056">
                  Food, beverage & quick service
                </h3>
              </div>
              <p className="text-gray-600 mb-6" data-id="element-1057">
                Fast-paced customer-facing spaces where comfort and reliable
                performance matter.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-1058">
                {['Subway', 'Wingers', 'Starbucks', 'Black Sheep Coffee', 'Esquires', 'Pepes Piri Piri', "Papa John's"].map((brand, i) => <span key={i} className="bg-white px-3 py-1 rounded-full text-sm font-medium text-primary-dark border border-gray-200" data-id="element-1059">
                    {brand}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: All Case Studies Grid */}
      <section className="py-20 bg-primary-light/30" data-id="element-1060">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1061">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-6" data-id="element-1062">
            <h2 className="text-3xl font-bold text-primary-dark" data-id="element-1063">
              Explore more projects
            </h2>

            <div className="flex flex-wrap gap-2" data-id="element-1064">
              {filters.map(filter => <button key={filter} onClick={() => setActiveFilter(filter)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeFilter === filter ? 'bg-primary-dark text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`} data-id="element-1065">
                  {filter}
                </button>)}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1066">
            {filteredProjects.map((project, idx) => <motion.div key={idx} layout initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.3
          }} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col h-full group hover:shadow-md transition-shadow" data-id="element-1067">
                <div className="text-xs font-semibold text-accent mb-3 uppercase tracking-wider" data-id="element-1068">
                  {project.sector}
                </div>
                <h3 className="text-lg font-bold text-primary-dark mb-3 group-hover:text-accent transition-colors" data-id="element-1069">
                  {project.title}
                </h3>
                <p className="text-gray-600 text-sm mb-6 flex-grow" data-id="element-1070">
                  {project.result}
                </p>
                <div className="flex flex-wrap gap-2 mb-6" data-id="element-1071">
                  {project.tags.map((tag, i) => <span key={i} className="bg-surface px-2 py-1 rounded-md text-[10px] font-medium text-gray-500" data-id="element-1072">
                      {tag}
                    </span>)}
                </div>
                <Link to={project.link} className="inline-flex items-center text-sm font-semibold text-primary-dark group-hover:text-accent transition-colors mt-auto" data-id="element-1073">
                  View case study <ArrowUpRight className="ml-1 w-4 h-4" data-id="element-1074" />
                </Link>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Section 6: Final CTA Band */}
      <section className="py-20 bg-primary-dark text-white text-center" data-id="element-1075">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1076">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1077">
            Planning a similar project?
          </h2>
          <p className="text-xl text-gray-300 mb-10" data-id="element-1078">
            Whether you need a bespoke installation, reliable maintenance, or
            TM44 compliance support, our commercial team is ready to help.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1079">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-white bg-accent hover:bg-accent/90 transition-colors" data-id="element-1080">
              Book a site survey
              <ArrowRight className="ml-2 w-5 h-5" data-id="element-1081" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-white border-2 border-white/20 hover:bg-white/10 transition-colors" data-id="element-1082">
              Talk to our commercial team
            </Link>
          </div>
        </div>
      </section>
    </div>;
}